import React from 'react';
import { X, CheckCircle2, AlertOctagon, AlertTriangle, AlertCircle, Info, ChevronUp } from 'lucide-react';
import { KeeperAssessment } from '../../types/geminiTypes';
import { cn } from '../../lib/utils';
import { motion } from 'motion/react';

interface KeeperCardProps {
  assessment: KeeperAssessment;
  onDismiss: () => void;
}

export function KeeperCard({ assessment, onDismiss }: KeeperCardProps) {
  const getBadgeColor = (status: 'KEEP' | 'WATCH' | 'EXIT' | 'N/A') => {
    switch (status) {
      case 'KEEP': return 'bg-success-50 text-success-700 border-success-100';
      case 'WATCH': return 'bg-warning-50 text-warning-700 border-warning-100';
      case 'EXIT': return 'bg-danger-50 text-danger-700 border-danger-100';
      default: return 'bg-surface-2 text-ink-600 border-ink-200';
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      className="oi-card overflow-hidden mt-4 mb-4"
    >
      {assessment.analysisStale && (
        <div className="bg-warning-50 border-b border-warning-100 px-4 py-2 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-warning-600" />
          <span className="text-xs font-medium text-warning-700">
            Note: This assessment is based on an Oracle analysis that is {assessment.analysisAge} days old.
          </span>
        </div>
      )}
      
      <div className="p-4 sm:p-6 border-b border-ink-200 flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <span className={cn("px-3 py-1 text-xs font-medium uppercase tracking-wider rounded border", getBadgeColor(assessment.verdict))}>
              {assessment.verdict}
            </span>
            <span className="text-sm font-medium text-ink">{assessment.companyName} ({assessment.ticker})</span>
          </div>
          <p className="text-sm text-ink-600 leading-relaxed max-w-3xl">
            {assessment.verdictRationale}
          </p>
        </div>
        <button 
          onClick={onDismiss}
          className="text-ink-500 hover:text-oxblood transition-colors rounded-md hover:bg-surface-2 p-1 shrink-0"
          title="Collapse Assessment"
        >
          <ChevronUp className="w-5 h-5" />
        </button>
      </div>

      <div className="p-4 sm:p-6 grid grid-cols-1 lg:grid-cols-2 gap-6 bg-surface-2">
        <div className="space-y-4">
          <h3 className="text-xs font-medium text-ink uppercase tracking-wider">Signal Evaluation</h3>
          <div className="grid grid-cols-2 gap-3 text-left">
            {assessment.signals.map((signal, idx) => (
              <div key={idx} className="bg-surface p-3 rounded-md border border-ink-200 flex flex-col gap-2 h-full">
                <div className="flex flex-row items-center justify-between gap-2 min-w-0">
                  <span className="text-[10px] font-medium uppercase tracking-wider text-ink-500 truncate flex-1 min-w-0">{signal.signalName}</span>
                  <span className={cn("px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider rounded border flex items-center gap-1", getBadgeColor(signal.status))}>
                    {signal.status}
                  </span>
                </div>
                <div>
                  <div className="text-sm font-medium text-ink mb-1">{signal.currentValue}</div>
                  <div className="text-xs text-ink-600 leading-relaxed">{signal.rationale}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-6 text-left pt-8">
          <div className="bg-surface p-4 sm:p-5 rounded-md border border-ink-200">
            <div className="flex items-start gap-3 mb-3">
              {assessment.thesisIntact ? (
                <div className="w-8 h-8 rounded-md bg-success-100 flex items-center justify-center shrink-0">
                  <CheckCircle2 className="w-5 h-5 text-success-700" />
                </div>
              ) : (
                <div className="w-8 h-8 rounded-md border border-danger-100 bg-danger-50 flex items-center justify-center shrink-0">
                  <X className="w-5 h-5 text-danger-600" />
                </div>
              )}
              <div>
                <h3 className="text-sm font-medium text-ink mb-1">
                  {assessment.thesisIntact ? "Core Thesis Intact" : "Core Thesis Broken"}
                </h3>
                <p className="text-xs text-ink-600 leading-relaxed">{assessment.thesisIntactRationale}</p>
              </div>
            </div>

            <div className="h-px bg-ink-200 my-4" />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <h4 className="text-[10px] font-medium text-warning-700 uppercase tracking-wider mb-2 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  What to Watch
                </h4>
                <ul className="space-y-1.5 pl-1 text-left">
                  {assessment.whatToWatch.map((item, idx) => (
                    <li key={idx} className="text-xs text-ink-600 flex items-start gap-1.5">
                      <span className="text-warning-600 mt-0.5">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="text-[10px] font-medium text-danger-700 uppercase tracking-wider mb-2 flex items-center gap-1">
                  <AlertOctagon className="w-3 h-3" />
                  Exit Triggers
                </h4>
                <ul className="space-y-1.5 pl-1 text-left">
                  {assessment.exitTriggers.map((item, idx) => (
                    <li key={idx} className="text-xs text-ink-600 flex items-start gap-1.5">
                      <span className="text-danger-600 mt-0.5">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-oxblood text-paper p-5 md:p-6 rounded-md">
            <h4 className="text-[10px] font-medium text-paper/60 uppercase tracking-widest mb-3">The 100-Year Test</h4>
            <p className="text-sm font-serif leading-relaxed text-paper/90">
              {assessment.hundredYearQuestion}
            </p>
          </div>
          
          {assessment.confidenceNote && (
             <div className="text-[10px] text-ink-500 flex gap-1 items-start bg-surface p-3 rounded-md border border-ink-200">
                <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <p>{assessment.confidenceNote}</p>
             </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
